// app/src/features/report/useReportPointerWiring.js
import { useActiveForecast } from "../../hooks/useActiveForecast.js";
import { useBriefPrefetch } from "../../hooks/useBriefPrefetch.js";
import { useReportListen } from "../../hooks/useReportListen.js";
import { useReportPointer, useLiveForecastReport } from "../../hooks/useReportPointer.js";
import { useReportPointerSeries } from "../../hooks/useReportPointerSeries.js";
import { useResolutionPrefetch } from "../../hooks/useResolutionPrefetch.js";
import { useSession } from "../../hooks/useSession.js";
import { rootEngine } from "../../lib/forecast/engines.js";
import {
  applyPointerOverrides,
  forecastPointers,
  liveContractMomentOf,
} from "../../lib/forecast/reportPointer.js";
import { resolutionReadWorthwhile } from "../../lib/report/resolutionRead.js";

/**
 * The report wiring's second point: the Listen briefing (`useReportListen`) and the report
 * pointer reads — the open modal's stored forecast and resolution (`useReportPointer`), the eager
 * live-report read that feeds the brief prefetch (`useLiveForecastReport` + `useBriefPrefetch`),
 * the resolution prefetch on selection (`useResolutionPrefetch`) and the probability plot's async
 * series (`useReportPointerSeries`). Separate from `useReportWiring` because it needs the
 * timeline framing's `autoZoom`, which the composition root only has later.
 *
 * Not here: the open state the reads are gated on (`showReport`, `useReportWiring`'s, an input).
 * `forecastTracking` (the Forecast Tracking gate) is the session context's
 * `_forecastTrackingAllowed`, read here through `useSession()`.
 *
 * @param {object} deps
 * @param {false|true|object} deps.showReport  the report modal's open state (useReportWiring's):
 *   `false` when closed, else the value `openReport` was given (`true`, or a root/branch target)
 * @param {Date} deps.effectiveToday  the timeline's "today" (the debug override applied)
 * @param {(rd: string, animate?: boolean, startDate?: string|null, buffer?: number|null, keepToday?: boolean) => void} deps.autoZoom
 *   the timeline framing's auto-zoom (the Listen player flies the view with it)
 * @param {React.MutableRefObject} deps.vlRef  the timeline's view-left ref
 * @param {React.MutableRefObject} deps.ppdRef  the timeline's pixels-per-day ref
 * @param {{current: {w: number}}} deps.cSizeRef  the timeline's canvas size ref
 * @param {{current: number|null}} deps.animRef  the timeline's shared animation frame
 * @param {() => void} deps.scheduleRender
 * @param {boolean} deps.railPanelOpen
 * @param {() => void} deps.toggleRailCollapse  the layout shell's rail toggle
 */
export function useReportPointerWiring({
  showReport,
  effectiveToday,
  autoZoom,
  vlRef,
  ppdRef,
  cSizeRef,
  animRef,
  scheduleRender,
  railPanelOpen,
  toggleRailCollapse,
}) {
  const { _forecastTrackingAllowed: forecastTracking } = useSession();
  const active = useActiveForecast();
  // The Listen briefing borrows the timeline for its "watch" scene (docs/report-listen.md).
  const reportListen = useReportListen({
    autoZoom,
    vlRef,
    ppdRef,
    cSizeRef,
    animRef,
    scheduleRender,
    railPanelOpen,
    toggleRail: toggleRailCollapse,
  });

  // When the report view is open, lazily fetch the active run's latest stored forecast report (via
  // its latest report_pointer's row_key) and latest conclusive resolution (via its
  // contractKey/qkey), both TTL-cached. The modal overlays these onto its server_ctx-derived source
  // (applyPointerOverrides) and falls back to the server_ctx render when there's no pointer or the
  // read 404s/204s. The forecast read is gated on showReport; the resolution read is gated
  // additionally on the contract being resolved, so opening a report on an unresolved forecast
  // never hits BigQuery via /resolve/report. Both reads are also gated on `forecastTracking`.
  const {
    forecastSource: pointerReportSource,
    resolution: pointerResolution,
    resolutionSettled: pointerResolutionSettled,
    rowKey: reportRowKey,
  } = useReportPointer({
    reportPointers: active?.reportPointers,
    contractKey: active?.contractKey,
    // The live contract moment — a latest pointer OLDER than this is stale (a manual
    // ↻ reforecast advanced the contract without writing a pointer), so the overlay
    // is skipped and "current" keeps its own report. The same moment the derive path and
    // the server use (liveContractMomentOf), so the report shown is the one a branch freezes.
    liveMomentIso: liveContractMomentOf(active),
    enabled: showReport && !!active && forecastTracking,
    // The gate mirrors the modal's own render gate (see lib/report/resolutionRead.js), so the
    // read fires exactly when the section could display it — no BigQuery traffic for a live,
    // in-window forecast. /api/resolve/report also serves a settled `unresolved` attempt, which a
    // not-yet-resolved contract can have.
    resolutionEnabled:
      showReport &&
      !!active &&
      forecastTracking &&
      resolutionReadWorthwhile({
        contractStatus: active?.contractStatus,
        resolutionDate: active?.contract?.resolution_date,
        today: effectiveToday,
        // Skips the read entirely once the server has cached the report onto the row.
        storedResolution: active?.resolution,
      }),
  });

  // The brief's lines from the report as the report window will show it: the latest saved fire
  // over the live forecast, so the answers written early are the ones it looks up.
  // Its own read (sharing the report cache), so a failed early read never stops the report
  // window's read from retrying when it opens. Read for every forecast on screen, not only a
  // Mirabilis one: the saved fire decides whether the report opens as the brief.
  const eagerPointerSource = useLiveForecastReport({
    reportPointers: active?.reportPointers,
    liveMomentIso: liveContractMomentOf(active),
    enabled: !!active && forecastTracking,
  });
  useBriefPrefetch({
    source: eagerPointerSource ? applyPointerOverrides(active, eagerPointerSource, null) : null,
    engine: active ? rootEngine(active) || null : null,
  });

  // Prefetches the same read on SELECTION rather than on modal open, so its latency overlaps the
  // user reading the timeline instead of the user waiting on a placeholder. Same gate as the read
  // above, so it can never fetch what the modal wouldn't.
  useResolutionPrefetch({
    contractKey: active?.contractKey,
    contractStatus: active?.contractStatus,
    resolutionDate: active?.contract?.resolution_date,
    storedResolution: active?.resolution,
    today: effectiveToday,
    enabled: forecastTracking,
  });

  // Async scheduled reforecasts for the probability plot. Unlike the single
  // report overlay above, this fetches EVERY forecast pointer's stored
  // prediction so ProbabilityPlot can draw them as a unified series with the
  // manual ↻ history. Gated on the same flag; unlike the overlay it does NOT
  // depend on the report modal being open (the plot is visible on its own), and
  // it only fetches when there are pointers to fetch.
  // Count only kind:'forecast' pointers — report_pointers also holds resolution
  // pointers, which are not reforecasts to plot. Gates both the fetch and the
  // plot render (an async-only contract plots iff it has a forecast pointer).
  const forecastPointerCount = forecastPointers(active?.reportPointers).length;
  // `shiftStabilitySummary` rides along from the same batched read: the shift verdicts arrive
  // in the same payload as the plot moments, so the aggregate costs no extra round-trip.
  const { asyncMoments, shiftStabilitySummary } = useReportPointerSeries({
    reportPointers: active?.reportPointers,
    enabled: forecastTracking && forecastPointerCount > 0,
  });

  return {
    reportListen,
    pointerReportSource,
    pointerResolution,
    pointerResolutionSettled,
    reportRowKey,
    forecastPointerCount,
    asyncMoments,
    shiftStabilitySummary,
  };
}
